import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Modal,
  Pagination
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { useListRequest } from '../hooks/useListRequest';
import { useAnchoredPortalMenu } from '../hooks/useAnchoredPortalMenu';
import { policyAPI, departmentAPI, employeeAPI, authAPI, isRequestCanceled } from '../utils/api';
import type { Policy, CreatePolicyRequest } from '../types/policy';
import type { Department } from '../types/department';
import type { EmployeeOption } from '../types/employee';
import type { User } from '../types/auth';
import { formatDateTimeInCompanyTimezone, getTodayDateInCompanyTimezone } from '../utils/timezone';
import './Policies.css';

// Icons
const PolicyIcon = () => (
  <i className="bi bi-shield-lock"></i>
);





// Custom MultiSelect Dropdown Component
interface MultiSelectOption {
  value: string | number;
  label: string;
  searchText?: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selectedValues: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  disabled = false,
  searchable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t: tc } = useTranslation('common');
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = React.useId();
  const { controlRef, menuRef, menuPosition } = useAnchoredPortalMenu({
    isOpen,
    preferredMaxHeight: 240,
    minWidth: 0,
  });
  const menuVisible = isOpen && !!menuPosition;
  useEffect(() => { if (menuVisible && searchable) searchRef.current?.focus(); }, [menuVisible, searchable]);

  const handleToggle = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionToggle = (value: string | number, e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.stopPropagation();
    }
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = (e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onChange([...new Set([...selectedValues, ...options.map(option => option.value)])]);
  };

  const handleDeselectAll = (e?: React.MouseEvent | React.TouchEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onChange([]);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      const clickedControl = controlRef.current?.contains(target);
      const clickedMenu = menuRef.current?.contains(target);
      if (!clickedControl && !clickedMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, controlRef, menuRef]);

  const displayText = selectedValues.length > 0 
    ? `${selectedValues.length} selected` 
    : placeholder;

  const [optionSearch, setOptionSearch] = useState('');
  const query = optionSearch.trim().toLocaleLowerCase();
  const visibleOptions = options.filter(option => selectedValues.includes(option.value)
    || !searchable || (option.searchText ?? option.label).toLocaleLowerCase().includes(query));
  const allSelected = options.length > 0 && options.every(option => selectedValues.includes(option.value));
  const someSelected = !allSelected && options.some(option => selectedValues.includes(option.value));

  const menu = isOpen && menuPosition
    ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          className="an-floating-menu an-multiselect-menu-portal an-policy-multiselect-menu"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
          }}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault(); event.stopPropagation();
              setIsOpen(false);
              controlRef.current?.querySelector('input')?.focus();
            }
            if (event.key === 'Enter' && event.target === searchRef.current) event.preventDefault();
          }}
        >
          <div className="an-policy-multiselect-header">
          {searchable && <Form.Control ref={searchRef} type="search" size="sm" value={optionSearch}
            aria-label={tc('buttons.search')} placeholder={tc('buttons.search')}
            onChange={event => setOptionSearch(event.target.value)} />}
          {options.length > 0 && (
            <div className="border-bottom">
              <div
                className="px-3 py-2 d-flex align-items-center"
                style={{ cursor: 'pointer', backgroundColor: 'var(--an-surface-muted)' }}
                onClick={(e) => {
                  if (e.target === e.currentTarget || e.target === e.currentTarget.querySelector('span')) {
                    if (allSelected) {
                      handleDeselectAll(e);
                    } else {
                      handleSelectAll(e);
                    }
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (allSelected) {
                      handleDeselectAll(e);
                    } else {
                      handleSelectAll(e);
                    }
                  }}
                  className="form-check-input me-2"
                  style={{
                    cursor: 'pointer',
                    transform: 'scale(1.1)',
                    accentColor: 'var(--an-primary)'
                  }}
                  key={`select-all-${allSelected}-${someSelected}`}
                />
                <span className="fw-semibold text-primary">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </span>
              </div>
            </div>
          )}
          </div>
          <div className="an-multiselect-options an-policy-multiselect-options">
          {visibleOptions.map((option) => (
            <div
              key={option.value}
              className="px-3 py-2 d-flex align-items-center an-policy-multiselect-option"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                if (e.target === e.currentTarget || e.target === e.currentTarget.querySelector('span')) {
                  handleOptionToggle(option.value, e);
                }
              }}
            >
              <input
                type="checkbox"
                aria-label={option.label}
                checked={selectedValues.includes(option.value)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleOptionToggle(option.value, e);
                }}
                className="form-check-input me-2"
                style={{
                  cursor: 'pointer',
                  transform: 'scale(1.1)',
                  accentColor: 'var(--an-primary)'
                }}
                key={`${option.value}-${selectedValues.includes(option.value)}`}
              />
              <span>{option.label}</span>
            </div>
          ))}
          </div>
        </div>,
        controlRef.current?.closest('.modal') ?? document.body
      )
    : null;

  return (
    <div ref={controlRef} className="position-relative">
      <div className="position-relative">
        <Form.Control
          type="text"
          role="combobox"
          aria-label={placeholder}
          aria-expanded={isOpen}
          aria-controls={menuId}
          className="an-policy-multiselect-control"
          value={displayText}
          onClick={handleToggle}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
              event.preventDefault(); setIsOpen(true);
            }
            if (event.key === 'Escape') setIsOpen(false);
          }}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
        <div 
          className="position-absolute"
          style={{ 
            insetInlineEnd: '12px',
            top: '50%', 
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: 'var(--an-text-secondary)'
          }}
        >
          <i className="bi bi-chevron-down" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}></i>
        </div>
      </div>
      {menu}
    </div>
  );
};


const ACTION_TYPE_KEYS: Record<string, string> = {
  enter_department: 'form.detected',
  not_detected: 'form.notDetected',
  detected: 'form.detectedGeneric',
  ppe_helmet_missing: 'form.ppeHelmetMissing',
  ppe_industrial_gloves_missing: 'form.ppeIndustrialGlovesMissing',
  ppe_industrial_jacket_missing: 'form.ppeIndustrialJacketMissing',
  ppe_hairnet_missing: 'form.ppeHairnetMissing',
  ppe_gloves_missing: 'form.ppeGlovesMissing',
  ppe_violation: 'form.ppeViolation',
  late_arrival: 'form.lateArrival',
  early_departure: 'form.earlyDeparture',
  still_working_after_hours: 'form.stillWorkingAfterHours',
  absent_working_day: 'form.absentWorkingDay',
  insufficient_hours: 'form.insufficientHours',
  unknown_face: 'form.unknownFace',
  after_hours_detected: 'form.afterHoursDetected',
  camera_offline: 'form.cameraOffline',
  repeated_ppe_violation: 'form.repeatedPpeViolation',
  restricted_hours_access: 'form.restrictedHoursAccess',
  occupancy_threshold: 'form.occupancyThreshold',
  consecutive_absence: 'form.consecutiveAbsence',
  break_overrun: 'form.breakOverrun',
  former_employee_detected: 'form.formerEmployeeDetected',
};

const ACTION_TYPE_GROUPS = [
  { labelKey: 'form.groupDetection', values: ['enter_department', 'detected', 'not_detected'] },
  { labelKey: 'form.groupPpe', values: ['ppe_helmet_missing', 'ppe_industrial_gloves_missing', 'ppe_industrial_jacket_missing', 'ppe_hairnet_missing', 'ppe_gloves_missing', 'ppe_violation'] },
  { labelKey: 'form.groupAttendance', values: ['late_arrival', 'early_departure', 'still_working_after_hours', 'absent_working_day', 'insufficient_hours'] },
  { labelKey: 'form.groupSecurity', values: ['unknown_face', 'after_hours_detected', 'camera_offline', 'repeated_ppe_violation', 'restricted_hours_access', 'former_employee_detected'] },
  { labelKey: 'form.groupAdvanced', values: ['occupancy_threshold', 'consecutive_absence', 'break_overrun'] },
];

const NOTIFY_CHANNELS = ['in_app', 'email', 'push'] as const;
const CHANNEL_LABEL_KEYS: Record<string, string> = {
  in_app: 'form.channelInApp',
  email: 'form.channelEmail',
  sms: 'form.channelSms',
  webhook: 'form.channelWebhook',
  push: 'form.channelPush',
  whatsapp: 'form.channelWhatsapp',
};

const filterNotifyChannels = (channels: string[] | undefined | null): string[] =>
  (channels || []).filter(channel => NOTIFY_CHANNELS.includes(channel as typeof NOTIFY_CHANNELS[number]));

const filterChannelRouting = (
  routing: CreatePolicyRequest['channel_routing']
): CreatePolicyRequest['channel_routing'] => {
  if (!routing) return null;

  const filtered = {
    red: filterNotifyChannels(routing.red),
    yellow: filterNotifyChannels(routing.yellow),
    grey: filterNotifyChannels(routing.grey),
  };

  const hasAny = filtered.red.length > 0 || filtered.yellow.length > 0 || filtered.grey.length > 0;
  return hasAny ? filtered : null;
};

const DEFAULT_CHANNEL_ROUTING = {
  red: [...NOTIFY_CHANNELS],
  yellow: ['in_app', 'push', 'email'],
  grey: ['in_app'],
};

const ACTION_TYPES_WITH_TIME_WINDOW = ['enter_department', 'restricted_hours_access', 'former_employee_detected'];
const TYPES_NEEDING_INACTIVE_EMPLOYEES: Array<CreatePolicyRequest['action_type']> = ['former_employee_detected', 'detected'];
const REQUIRED_CONDITION_FIELDS: Record<string, Array<keyof CreatePolicyRequest>> = {
  camera_offline: ['offline_threshold_minutes'],
  repeated_ppe_violation: ['violation_count_threshold', 'violation_window_hours'],
  occupancy_threshold: ['max_occupancy', 'occupancy_window_minutes'],
  consecutive_absence: ['consecutive_days_threshold'],
  break_overrun: ['max_break_minutes'],
};

const TYPE_SPECIFIC_FIELDS: Array<keyof CreatePolicyRequest> = [
  'grace_extra_minutes',
  'check_after_minutes',
  'offline_threshold_minutes',
  'violation_count_threshold',
  'violation_window_hours',
  'max_occupancy',
  'occupancy_window_minutes',
  'consecutive_days_threshold',
  'max_break_minutes',
];

const formatEmployeeOption = (emp: EmployeeOption, todayDate: string, inactiveLabel: string): MultiSelectOption => {
  const endOfHireDate = emp.end_of_hire_date?.split('T')[0];
  const hasPastEndDate = !!endOfHireDate && endOfHireDate < todayDate;
  const isInactive = emp.is_active_today === false || (emp.is_active_today === undefined && hasPastEndDate);
  const label = `${emp.name} (${emp.department})`;

  return {
    value: emp.id,
    searchText: `${emp.name} ${emp.emp_id} ${emp.department}`,
    label: isInactive ? (endOfHireDate ? `${label} \u2014 ended ${endOfHireDate}` : `${label} (${inactiveLabel})`) : label,
  };
};


const Policies: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const { canAdd, canEdit, canDelete, canView } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'policies' });
  const { t: tc } = useTranslation('common');
  
  const actionTypeLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, translationKey] of Object.entries(ACTION_TYPE_KEYS)) {
      labels[key] = t(translationKey);
    }
    return labels;
  }, [t]);

  const channelOptions = useMemo(() => NOTIFY_CHANNELS.map(channel => ({
    value: channel,
    label: t(CHANNEL_LABEL_KEYS[channel]),
  })), [t]);

  // Data state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [allEmployees, setAllEmployees] = useState<EmployeeOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { begin: beginPolicies, cancel: cancelPolicies } = useListRequest(currentCompany?.company_id || '');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [viewingPolicy, setViewingPolicy] = useState<Policy | null>(null);
  const [deletingPolicy, setDeletingPolicy] = useState<Policy | null>(null);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  
  // Form state for create
  const [formData, setFormData] = useState<CreatePolicyRequest>({
    company_id: currentCompany?.company_id || '',
    name: '',
    is_enabled: true,
    alert_level: 'grey',
    notify_channels: ['in_app'],
    action_type: 'enter_department',
    detection_method: 'department',
    time_mode: 'anytime',
    days_of_week: [],
    start_time: '09:00',
    end_time: '17:00',
    date_start: '',
    date_end: '',
    timeslap: 15,
    ppe_confidence_threshold: 0.8,
    repeat_interval_minutes: 180,
    departments: [],
    locations: [],
    employee_ids: [],
    user_ids: [],
    channel_routing: null,
    digest_mode: null,
    escalation_minutes: null,
    escalation_user_ids: null,
    policy_employee_recipients: []
  });

  const editorOpen = showCreateModal || showEditModal;
  const employeeDepartmentId = formData.departments?.length === 1 ? formData.departments[0] : undefined;
  const selectedIdsRef = useRef<number[]>([]);
  selectedIdsRef.current = [...(formData.employee_ids || []), ...(formData.policy_employee_recipients || [])];
  useEffect(() => {
    setEmployees([]);
    setAllEmployees([]);
  }, [currentCompany?.company_id]);
  useEffect(() => {
    if (!editorOpen || !currentCompany?.company_id) return;
    const controller = new AbortController();
    const params = { fields: 'minimal' as const, ...(employeeDepartmentId ? { department_id: employeeDepartmentId } : {}) };
    const retainSelections = (previous: EmployeeOption[], loaded: EmployeeOption[]) => {
      const merged = new Map(previous.filter(employee => selectedIdsRef.current.includes(employee.id)).map(employee => [employee.id, employee]));
      loaded.forEach(employee => merged.set(employee.id, employee));
      return [...merged.values()];
    };
    void (async () => {
      try {
        const active = await employeeAPI.getEmployees(currentCompany.company_id, params, controller.signal);
        if (controller.signal.aborted) return;
        if (active.success) {
          setEmployees(previous => retainSelections(previous, active.employees || []));
          setAllEmployees(previous => retainSelections(previous, active.employees || []));
        }
        try {
          const all = await employeeAPI.getEmployees(currentCompany.company_id, { ...params, all_employees: 1 }, controller.signal);
          if (!controller.signal.aborted && all.success && active.success) {
            const activeIds = new Set((active.employees || []).map(employee => employee.id));
            setAllEmployees(previous => retainSelections(previous, (all.employees || []).map(employee => ({
              ...employee, is_active_today: employee.is_active_today ?? activeIds.has(employee.id),
            }))));
          }
        } catch (err) {
          if (!controller.signal.aborted && !isRequestCanceled(err)) console.error('Error fetching all employees:', err);
        }
      } catch (err) {
        if (!controller.signal.aborted && !isRequestCanceled(err)) console.error('Error loading employees:', err);
      }
    })();
    return () => controller.abort();
  }, [editorOpen, currentCompany?.company_id, employeeDepartmentId]);

  const employeeOptions = useMemo(() => {
    const todayDate = getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC');
    const list = TYPES_NEEDING_INACTIVE_EMPLOYEES.includes(formData.action_type)
      ? allEmployees
      : employees;

    return list.map(emp => formatEmployeeOption(emp, todayDate, tc('labels.inactive')));
  }, [employees, allEmployees, formData.action_type, currentCompany?.timezone, tc]);

  const activeEmployeeOptions = useMemo(() => {
    const todayDate = getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC');
    return employees.map(emp => formatEmployeeOption(emp, todayDate, tc('labels.inactive')));
  }, [employees, currentCompany?.timezone, tc]);

  const inactiveEmployeeIds = useMemo(() => new Set(allEmployees
    .filter(employee => employee.is_active_today === false).map(employee => employee.id)), [allEmployees]);

  // Absence from a loading or department-filtered list does not establish that
  // a saved target is inactive. Remove only employees whose status is known.
  useEffect(() => {
    if (!editorOpen || TYPES_NEEDING_INACTIVE_EMPLOYEES.includes(formData.action_type) || !inactiveEmployeeIds.size) return;
    setFormData(previous => {
      const ids = previous.employee_ids || [];
      const activeIds = ids.filter(id => !inactiveEmployeeIds.has(id));
      return activeIds.length === ids.length ? previous : { ...previous, employee_ids: activeIds };
    });
  }, [editorOpen, formData.action_type, inactiveEmployeeIds]);

  const userOptions = useMemo(() => users.map(user => ({
    value: user.id,
    label: `${user.name} (${user.email})`,
  })), [users]);

  // Load data
  const loadPolicies = useCallback(async () => {
    if (!currentCompany?.company_id) return;
    const { signal } = beginPolicies();
    if (signal.aborted) return;
    try {
      setLoading(true);
      setPolicies([]);
      setError(null);
      
      const response = await policyAPI.getPolicies(currentCompany.company_id, {}, signal);
      if (signal.aborted) return;
      
      // Handle both array response and object response
      if (Array.isArray(response)) {
        setPolicies(response);
      } else {
        setPolicies([]);
      }
    } catch (err: any) {
      if (signal.aborted || isRequestCanceled(err)) return;
      setError(err.message || t('loadFailed'));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [currentCompany?.company_id, t, beginPolicies]);

  const loadDropdownData = useCallback(async (signal: AbortSignal) => {
    if (!currentCompany?.company_id) return;
    setDepartments([]);
    setUsers([]);
    try {
      const departmentsResponse = await departmentAPI.getDepartments(currentCompany.company_id, signal);
      if (signal.aborted) return;
      setDepartments(departmentsResponse.departments || []);

      // Fetch users using the new endpoint
      try {
        const usersData = await authAPI.getUsers(currentCompany.company_id, {}, signal);
        if (signal.aborted) return;
        if (usersData.success) setUsers(usersData.data || []);
      } catch (userError) {
        if (signal.aborted || isRequestCanceled(userError)) return;
        console.error('Error fetching users:', userError);
      }
    } catch (err) {
      if (signal.aborted || isRequestCanceled(err)) return;
      console.error('Error loading dropdown data:', err);
    }
  }, [currentCompany?.company_id]);

  // Filtered and paginated data
  const filteredPolicies = useMemo(() => {
    let filtered = policies;
    
    if (searchTerm) {
      filtered = filtered.filter(policy => 
        policy.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  }, [policies, searchTerm]);

  const paginatedPolicies = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredPolicies.slice(startIndex, endIndex);
  }, [filteredPolicies, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredPolicies.length / rowsPerPage);

  const isTimeWindowAction = (actionType: string) => ACTION_TYPES_WITH_TIME_WINDOW.includes(actionType);

  const handleActionTypeChange = (actionType: CreatePolicyRequest['action_type']) => {
    setFormData(prev => {
      const next: CreatePolicyRequest = {
        ...prev,
        action_type: actionType,
        time_mode: isTimeWindowAction(actionType) ? prev.time_mode : 'anytime',
      };

      TYPE_SPECIFIC_FIELDS.forEach(field => {
        (next as any)[field] = null;
      });

      return next;
    });
  };

  const validatePolicyConditions = (): boolean => {
    const requiredFields = REQUIRED_CONDITION_FIELDS[formData.action_type] || [];
    const missingFields = requiredFields.filter(field => {
      const value = formData[field] as any;
      return value === null || value === undefined || value === '';
    });

    if (missingFields.length > 0) {
      setError(t('form.requiredConditionFields', { defaultValue: 'Please fill the required condition fields.' }));
      return false;
    }

    if (formData.action_type === 'late_arrival' && formData.grace_extra_minutes != null && formData.grace_extra_minutes < 0) {
      setError(t('form.invalidConditionFields', { defaultValue: 'Condition values must be valid numbers.' }));
      return false;
    }

    return true;
  };

  const buildPolicyPayload = (includeCompany = true): CreatePolicyRequest | Omit<CreatePolicyRequest, 'company_id'> => {
    const isPpeMissing = formData.action_type.startsWith('ppe_');
    const usesTimeWindow = isTimeWindowAction(formData.action_type);
    const hasRouting = !!formData.channel_routing;

    const payload: CreatePolicyRequest = {
      ...formData,
      notify_channels: hasRouting ? [] : [...new Set(filterNotifyChannels(formData.notify_channels))],
      channel_routing: hasRouting ? filterChannelRouting(formData.channel_routing) ?? undefined : undefined,
      digest_mode: formData.digest_mode || undefined,
      escalation_minutes: formData.escalation_minutes || undefined,
      escalation_user_ids: formData.escalation_minutes ? formData.escalation_user_ids || [] : undefined,
      policy_employee_recipients: formData.policy_employee_recipients?.length ? [...new Set(formData.policy_employee_recipients)] : undefined,
      user_ids: [...new Set(formData.user_ids)],
      locations: [...new Set(formData.locations)],
      days_of_week: usesTimeWindow && formData.time_mode === 'within_window' ? formData.days_of_week : undefined,
      time_mode: usesTimeWindow ? formData.time_mode : undefined,
      timeslap: (formData.action_type === 'not_detected' || isPpeMissing) ? formData.timeslap : undefined,
      ppe_confidence_threshold: isPpeMissing ? (formData.ppe_confidence_threshold ?? 0.8) : undefined,
      start_time: usesTimeWindow && formData.time_mode === 'within_window' ? formData.start_time : undefined,
      end_time: usesTimeWindow && formData.time_mode === 'within_window' ? formData.end_time : undefined,
      date_start: usesTimeWindow && formData.time_mode === 'within_window' ? formData.date_start : undefined,
      date_end: usesTimeWindow && formData.time_mode === 'within_window' ? formData.date_end : undefined,
      repeat_interval_minutes: formData.repeat_interval_minutes || 180,
      departments: formData.detection_method === 'department' ? [...new Set(formData.departments || [])] : undefined,
      employee_ids: formData.detection_method === 'employee' ? [...new Set(formData.employee_ids || [])] : undefined,
    };

    TYPE_SPECIFIC_FIELDS.forEach(field => {
      const shouldKeep =
        (formData.action_type === 'late_arrival' && field === 'grace_extra_minutes') ||
        (formData.action_type === 'absent_working_day' && field === 'check_after_minutes') ||
        REQUIRED_CONDITION_FIELDS[formData.action_type]?.includes(field);
      if (!shouldKeep || payload[field] === null || payload[field] === undefined || payload[field] === '') {
        delete (payload as any)[field];
      }
    });

    if (!includeCompany) {
      const { company_id, ...withoutCompany } = payload;
      return withoutCompany;
    }

    return payload;
  };

  // Event handlers
  const handleCreatePolicy = async () => {
    try {
      setLoading(true);
      if (!validatePolicyConditions()) return;
      
      const requestData = buildPolicyPayload(true) as CreatePolicyRequest;

      // Debug: Log the request data
      console.log('ðŸ“¤ Creating policy with data:', requestData);

      const response = await policyAPI.createPolicy(requestData);
      
      if (response.success) {
        setShowCreateModal(false);
        resetForm();
        loadPolicies();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('createFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle edit policy
  const handleEditPolicy = (policy: Policy) => {
    setEditingPolicy(policy);
    
    // Helper function to safely parse days_of_week
    const parseDaysOfWeek = (days: any): string[] => {
      if (!days) return [];
      if (Array.isArray(days)) return days;
      if (typeof days === 'string') return days.split(',').map(d => d.trim()).filter(d => d);
      return [];
    };
    
    // Pre-fill form with policy data
    setFormData({
      company_id: policy.company_id,
      name: policy.name,
      is_enabled: policy.is_enabled,
      alert_level: policy.alert_level,
      notify_channels: filterNotifyChannels(policy.notify_channels),
      action_type: policy.action_type,
      detection_method: policy.detection_method,
      time_mode: policy.time_mode || 'anytime',
      days_of_week: parseDaysOfWeek(policy.days_of_week),
      start_time: formatTimeForInput(policy.start_time),
      end_time: formatTimeForInput(policy.end_time),
      date_start: formatDateForInput(policy.date_start),
      date_end: formatDateForInput(policy.date_end),
      timeslap: policy.timeslap || 15,
      ppe_confidence_threshold: policy.ppe_confidence_threshold ?? 0.8,
      repeat_interval_minutes: policy.repeat_interval_minutes || 180,
      grace_extra_minutes: policy.grace_extra_minutes ?? null,
      check_after_minutes: policy.check_after_minutes ?? null,
      offline_threshold_minutes: policy.offline_threshold_minutes ?? null,
      violation_count_threshold: policy.violation_count_threshold ?? null,
      violation_window_hours: policy.violation_window_hours ?? null,
      max_occupancy: policy.max_occupancy ?? null,
      occupancy_window_minutes: policy.occupancy_window_minutes ?? null,
      consecutive_days_threshold: policy.consecutive_days_threshold ?? null,
      max_break_minutes: policy.max_break_minutes ?? null,
      channel_routing: filterChannelRouting(policy.channel_routing),
      digest_mode: policy.digest_mode || null,
      escalation_minutes: policy.escalation_minutes ?? null,
      escalation_user_ids: policy.escalation_user_ids || [],
      policy_employee_recipients: policy.policy_employee_recipients || [],
      departments: policy.departments?.map(d => d.department_id) || [],
      locations: policy.locations?.map(l => l.department_id) || [],
      employee_ids: policy.targets?.map(t => t.employee_id) || [],
      user_ids: policy.recipients?.map(r => r.user_id) || []
    });
    
    setShowEditModal(true);
  };

  // Handle edit policy submission
  const handleEditSubmit = async () => {
    if (!editingPolicy) return;
    
    try {
      setLoading(true);
      if (!validatePolicyConditions()) return;
      
      const requestData = buildPolicyPayload(false) as Omit<CreatePolicyRequest, 'company_id'>;

      // Debug: Log the request data
      console.log('ðŸ“¤ Updating policy with data:', requestData);

      const response = await policyAPI.updatePolicy(editingPolicy.id, requestData);
      
      if (response.success) {
        setShowEditModal(false);
        setEditingPolicy(null);
        resetForm();
        loadPolicies();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert backend time format to HTML input format (HH:MM)
  const formatTimeForInput = (time: string | undefined): string => {
    if (!time) return '09:00';
    
    // If already in HH:MM format, return as is
    if (/^\d{2}:\d{2}$/.test(time)) {
      return time;
    }
    
    // Handle HH:MM:SS format (e.g., "09:00:00" to "09:00")
    if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time.substring(0, 5); // Take first 5 characters (HH:MM)
    }
    
    // Convert H:i to HH:MM (e.g., "9:00" to "09:00")
    const parts = time.split(':');
    if (parts.length === 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    return '09:00';
  };

  // Helper function to format date for input (convert ISO datetime to YYYY-MM-DD)
  const formatDateForInput = (date: string | undefined): string => {
    if (!date) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    // Try to parse and format the date
    try {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0]; // YYYY-MM-DD
      }
    } catch (error) {
      console.log('âš ï¸ Error parsing date:', date, error);
    }
    
    return '';
  };

  // Handle view policy
  const handleViewPolicy = async (policy: Policy) => {
    try {
      setViewModalLoading(true);
      setViewingPolicy(null); // Clear previous data
      
      const response = await policyAPI.getPolicy(policy.id);
      
      if (response.success) {
        setViewingPolicy(response.policy);
        setShowViewModal(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('loadDetailsFailed'));
    } finally {
      setViewModalLoading(false);
    }
  };

  // Handle delete policy
  const handleDeletePolicy = async () => {
    if (!deletingPolicy) return;
    
    try {
      setLoading(true);
      
      const response = await policyAPI.deletePolicy(deletingPolicy.id);
      
      if (response.success) {
        setShowDeleteModal(false);
        setDeletingPolicy(null);
        loadPolicies();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Close view modal
  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingPolicy(null);
    setViewModalLoading(false);
  };

  const resetForm = () => {
    setFormData({
      company_id: currentCompany?.company_id || '',
      name: '',
      is_enabled: true,
      alert_level: 'grey',
      notify_channels: ['in_app'],
      action_type: 'enter_department',
      detection_method: 'department',
      time_mode: 'anytime',
      days_of_week: [],
      start_time: '09:00',
      end_time: '17:00',
      date_start: '',
      date_end: '',
      timeslap: 15,
      ppe_confidence_threshold: 0.8,
      repeat_interval_minutes: 180,
      departments: [],
      locations: [],
      employee_ids: [],
      user_ids: [],
      channel_routing: null,
      digest_mode: null,
      escalation_minutes: null,
      escalation_user_ids: null,
      policy_employee_recipients: []
    });
  };

  // Effects
  useEffect(() => {
    setSearchTerm('');
    setCurrentPage(1);
    if (currentCompany?.company_id) {
      setFormData(prev => ({ ...prev, company_id: currentCompany.company_id }));
    }
  }, [currentCompany]);

  useEffect(() => {
    void loadPolicies();
    return cancelPolicies;
  }, [loadPolicies, cancelPolicies]);

  useEffect(() => {
    const controller = new AbortController();
    void loadDropdownData(controller.signal);
    return () => controller.abort();
  }, [loadDropdownData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Show loading state while role permissions are being loaded
  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loadingPermissions')}</span>
          </div>
        </Container>
      </Layout>
    );
  }

  // Check if user has permission to view policies
  if (!canView('policy')) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <Alert variant="danger">
            {t('noAccess')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  if (!currentCompany) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="an-enterprise-page an-page-shell an-data-page">
          <Alert variant="warning">
            {t('selectCompany')}
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        {/* Header */}
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('policy') && (
            <Button 
              variant="primary" 
              onClick={() => setShowCreateModal(true)}
            >
              <i className="bi bi-plus-lg"></i>
              {t('createPolicy')}
            </Button>
          )}
        </div>

        <Alert variant="info" className="mb-4">
          <strong>{t('managingFor')}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
        </Alert>

        {error && (
          <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-4 an-data-card">
          <Card.Header className="an-data-card">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('filtersTitle')}</h5>
              <small className="text-muted">
                {tc('labels.company')}: {currentCompany.description}
              </small>
            </div>
          </Card.Header>
          <Card.Body className="an-data-card">
            <Form onSubmit={(e) => { e.preventDefault(); }}>
              <Row>
                <Col lg={3} md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('searchPlaceholder')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button type="button" variant="outline-secondary" onClick={() => setSearchTerm('')}>
                      {tc('buttons.clearFilters')}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Policies Table */}
        <Card className="mb-4 an-data-card">
          <Card.Header className="an-data-card">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                {t('policyRecords', { count: filteredPolicies.length })}
              </h5>
            </div>
          </Card.Header>
          <Card.Body className="p-0 an-data-card">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="me-3" />
                <span>{t('loading')}</span>
              </div>
            ) : paginatedPolicies.length === 0 ? (
              <div className="text-center py-4 py-md-5">
                <div style={{ fontSize: '3rem', opacity: '0.3', marginBottom: '1rem' }}>📋</div>
                <h5 className="an-empty-title">{t('noPoliciesTitle')}</h5>
                <p className="an-empty-text mb-0">
                  {searchTerm ? t('noMatch') : t('noneCreated')}
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="d-none d-md-block">
                  <div className="table-responsive an-data-table">
                    <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('columns.name')}</th>
                        <th>{t('columns.status')}</th>
                        <th>{t('columns.alertLevel')}</th>
                        <th>{t('columns.actionType')}</th>
                        <th>{t('columns.detectionMethod')}</th>
                        <th>{t('columns.locations')}</th>
                        <th>{t('columns.recipients')}</th>
                        <th>{t('columns.targets')}</th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPolicies.map((policy) => (
                          <tr key={policy.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <PolicyIcon />
                                <span className="ms-2 an-body-text">{policy.name}</span>
                              </div>
                            </td>
                            <td>
                              <Badge bg={policy.is_enabled ? 'success' : 'secondary'}>
                                {policy.is_enabled ? t('enabled') : t('disabled')}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={policy.alert_level === 'red' ? 'danger' : 
                                       policy.alert_level === 'yellow' ? 'warning' : 'secondary'}>
                                {policy.alert_level.toUpperCase()}
                              </Badge>
                            </td>
                            <td>
                              <small className="text-muted">
                                {actionTypeLabels[policy.action_type] || policy.action_type}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {policy.detection_method === 'department' ? t('byDepartment') : t('byEmployee')}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {t('locationCount', { count: policy.locations?.length || 0 })}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {t('userCount', { count: policy.recipients?.length || 0 })}
                              </small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {policy.detection_method === 'department' 
                                  ? t('deptCount', { count: policy.departments?.length || 0 })
                                  : t('empCount', { count: policy.targets?.length || 0 })
                                }
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {canView('policy') && (
                                  <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => handleViewPolicy(policy)} title={t('viewPolicy')}><i className="bi bi-eye"></i> <span className="ms-1">{tc('buttons.view')}</span></Button>
                                )}
                                {canEdit('policy') && (
                                  <Button variant="link" size="sm" className="text-primary p-1 me-3 text-decoration-none" onClick={() => handleEditPolicy(policy)} title={t('editPolicy')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
                                )}
                                {canDelete('policy') && (
                                  <Button variant="link" size="sm" className="text-danger p-1 text-decoration-none" onClick={() => { setDeletingPolicy(policy); setShowDeleteModal(true); }} title={t('deletePolicy')}><i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span></Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="d-md-none">
                  {paginatedPolicies.map((policy) => (
                    <Card key={policy.id} className="mb-3 border-0 shadow-sm an-data-card">
                      <Card.Body className="p-3 an-data-card">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-bold">{policy.name}</h6>
                            <small className="text-muted">{actionTypeLabels[policy.action_type] || policy.action_type}</small>
                          </div>
                          <Badge bg={policy.is_enabled ? 'success' : 'secondary'} className="px-2 py-1">
                            {policy.is_enabled ? t('enabled') : t('disabled')}
                          </Badge>
                        </div>
                        <Row className="g-2">
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.alertLevel')}</small>
                            <Badge bg={policy.alert_level === 'red' ? 'danger' : 
                                     policy.alert_level === 'yellow' ? 'warning' : 'secondary'} className="px-2 py-1">
                              {policy.alert_level.toUpperCase()}
                            </Badge>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.detectionMethod')}</small>
                            <small className="fw-medium">
                              {policy.detection_method === 'department' ? t('byDepartment') : t('byEmployee')}
                            </small>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.locations')}</small>
                            <span className="fw-medium text-primary">
                              {t('locationCount', { count: policy.locations?.length || 0 })}
                            </span>
                          </Col>
                          <Col xs={6}>
                            <small className="text-muted d-block">{t('columns.recipients')}</small>
                            <small className="text-muted">
                              {t('userCount', { count: policy.recipients?.length || 0 })}
                            </small>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2 mt-3 pt-2 border-top">
                          {canView('policy') && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleViewPolicy(policy)}
                              className="d-flex align-items-center"
                            >
                              <i className="bi bi-eye"></i>
                              <span className="ms-1">{tc('buttons.view')}</span>
                            </Button>
                          )}
                          {canEdit('policy') && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditPolicy(policy)}
                              className="d-flex align-items-center"
                            >
                              <i className="bi bi-pencil"></i>
                              <span className="ms-1">{tc('buttons.edit')}</span>
                            </Button>
                          )}
                          {canDelete('policy') && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setDeletingPolicy(policy);
                                setShowDeleteModal(true);
                              }}
                              className="d-flex align-items-center"
                            >
                              <i className="bi bi-trash"></i>
                              <span className="ms-1">{tc('buttons.delete')}</span>
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {/* Pagination */}
                {filteredPolicies.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-muted small">
                        {tc('pagination.showing', { from: ((currentPage - 1) * rowsPerPage) + 1, to: Math.min(currentPage * rowsPerPage, filteredPolicies.length), total: filteredPolicies.length })}
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">{tc('pagination.itemsPerPage')}</span>
                        <Form.Select
                          size="sm"
                          style={{ width: 'auto' }}
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </Form.Select>
                      </div>
                    </div>
                    <Pagination className="mb-0 an-pagination">
                      <Pagination.Prev 
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next 
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Create Policy Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('createNewPolicy')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleCreatePolicy(); }}>
            <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.companyId')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.company_id}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.policyName')} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('form.enterPolicyName')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.alertLevel')} *</Form.Label>
                    <Form.Select
                      value={formData.alert_level}
                      onChange={(e) => setFormData(prev => ({ ...prev, alert_level: e.target.value as any }))}
                      required
                    >
                      <option value="grey">{t('form.grey')}</option>
                      <option value="yellow">{t('form.yellow')}</option>
                      <option value="red">{t('form.red')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('columns.status')}</Form.Label>
                    <Form.Check
                      type="switch"
                      label={t('enabled')}
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.actionType')} *</Form.Label>
                    <Form.Select
                      value={formData.action_type}
                      onChange={(e) => handleActionTypeChange(e.target.value as CreatePolicyRequest['action_type'])}
                    >
                      {ACTION_TYPE_GROUPS.map(group => (
                        <optgroup key={group.labelKey} label={t(group.labelKey)}>
                          {group.values.map(actionType => (
                            <option key={actionType} value={actionType}>
                              {actionTypeLabels[actionType]}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Form.Select>
                    {formData.action_type === 'former_employee_detected' && (
                      <Alert variant="info" className="mb-3">
                        {t('form.formerEmployeeDetectedHint')}
                      </Alert>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.targetedLocations')} *</Form.Label>
                    <MultiSelectDropdown
                      options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                      selectedValues={formData.locations}
                      onChange={(values) => setFormData(prev => ({ ...prev, locations: values as number[] }))}
                      placeholder={t('form.selectLocations')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.detectionMethod')} *</Form.Label>
                    <Form.Select
                      value={formData.detection_method}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        detection_method: e.target.value as any,
                        departments: [],
                        employee_ids: []
                      }))}
                    >
                      <option value="department">{t('byDepartment')}</option>
                      <option value="employee">{t('byEmployee')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Time Mode for Detected */}
                {isTimeWindowAction(formData.action_type) && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeMode')} *</Form.Label>
                      <Form.Select
                        value={formData.time_mode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          time_mode: e.target.value as any
                        }))}
                      >
                        <option value="anytime">{t('form.anytime')}</option>
                        <option value="within_window">{t('form.withinWindow')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Timeslap for Not Detected */}
              {(formData.action_type === 'not_detected') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeInterval')} *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.timeslap}
                        onChange={(e) => setFormData(prev => ({ ...prev, timeslap: Number(e.target.value) }))}
                        placeholder={t('form.timeIntervalPlaceholder')}
                      />
                      <Form.Text className="text-muted">
                        {t('form.timeIntervalHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* PPE Confidence Threshold */}
              {formData.action_type.startsWith('ppe_') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        {t('form.confidenceThreshold')}: {Math.round((formData.ppe_confidence_threshold ?? 0.8) * 100)}%
                      </Form.Label>
                      <Form.Range
                        min="0"
                        max="1"
                        step="0.05"
                        value={formData.ppe_confidence_threshold ?? 0.8}
                        onChange={(e) => setFormData(prev => ({ ...prev, ppe_confidence_threshold: parseFloat(e.target.value) }))}
                      />
                      <Form.Text className="text-muted">
                        {t('form.confidenceThresholdHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {(formData.action_type === 'late_arrival' || formData.action_type === 'absent_working_day' || formData.action_type === 'camera_offline' || formData.action_type === 'consecutive_absence' || formData.action_type === 'break_overrun') && (
                <Row>
                  {formData.action_type === 'late_arrival' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.graceExtraMinutes')}</Form.Label>
                        <Form.Control type="number" min="0" value={formData.grace_extra_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, grace_extra_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'absent_working_day' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.checkAfterMinutes')}</Form.Label>
                        <Form.Control type="number" min="1" value={formData.check_after_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, check_after_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'camera_offline' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.offlineThresholdMinutes')} *</Form.Label>
                        <Form.Control type="number" min="5" value={formData.offline_threshold_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, offline_threshold_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'consecutive_absence' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.consecutiveDaysThreshold')} *</Form.Label>
                        <Form.Control type="number" min="2" value={formData.consecutive_days_threshold ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, consecutive_days_threshold: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'break_overrun' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.maxBreakMinutes')} *</Form.Label>
                        <Form.Control type="number" min="1" value={formData.max_break_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, max_break_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              )}

              {(formData.action_type === 'repeated_ppe_violation' || formData.action_type === 'occupancy_threshold') && (
                <Row>
                  {formData.action_type === 'repeated_ppe_violation' && (
                    <>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.violationCountThreshold')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.violation_count_threshold ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, violation_count_threshold: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.violationWindowHours')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.violation_window_hours ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, violation_window_hours: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                  {formData.action_type === 'occupancy_threshold' && (
                    <>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.maxOccupancy')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.max_occupancy ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, max_occupancy: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.occupancyWindowMinutes')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.occupancy_window_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, occupancy_window_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </Row>
              )}

              {/* Time Window Fields */}
              {isTimeWindowAction(formData.action_type) && formData.time_mode === 'within_window' && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.startTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.endTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateStart')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_start}
                          min={getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_start: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateEnd')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_end}
                          min={formData.date_start || getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Form.Label>{t('form.daysOfWeek')}</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <Form.Check
                          key={day}
                          type="checkbox"
                          label={day}
                          checked={formData.days_of_week?.includes(day)}
                          onChange={(e) => {
                            const currentDays = formData.days_of_week || [];
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: [...currentDays, day]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: currentDays.filter(d => d !== day)
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Target Selection */}
              {formData.detection_method === 'department' ? (
              <div className="mb-3">
                <Form.Label>{t('form.targetedByDepartments')} *</Form.Label>
                  <MultiSelectDropdown
                    options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                    selectedValues={formData.departments || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, departments: values as number[] }))}
                  placeholder={t('form.selectDepartments')}
                />
              </div>
              ) : (
              <div className="mb-3">
                <Form.Label>{t('form.targetEmployees')} *</Form.Label>
                  <MultiSelectDropdown
                    searchable options={employeeOptions}
                    selectedValues={formData.employee_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, employee_ids: values as number[] }))}
                  placeholder={t('form.selectEmployees')}
                />
                {formData.action_type === 'former_employee_detected' && (
                  <Form.Text className="text-muted">{t('form.formerEmployeeTargetHint')}</Form.Text>
                )}
              </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.notificationRecipients')} *</Form.Label>
                <MultiSelectDropdown
                  options={users.map(user => ({ value: user.id, label: `${user.name} (${user.email})` }))}
                  selectedValues={formData.user_ids}
                  onChange={(values) => setFormData(prev => ({ ...prev, user_ids: values as number[] }))}
                  placeholder={t('form.selectUsers')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationChannels')} *</Form.Label>
                <MultiSelectDropdown
                  options={channelOptions}
                  selectedValues={formData.notify_channels}
                  onChange={(values) => setFormData(prev => ({ ...prev, notify_channels: values as string[] }))}
                  placeholder={t('form.selectChannels')}
                />
              </div>

              <div className="mb-3">
                <Form.Check
                  type="switch"
                  label={t('form.perLevelRouting')}
                  checked={!!formData.channel_routing}
                  onChange={(e) => setFormData(prev => ({ ...prev, channel_routing: e.target.checked ? DEFAULT_CHANNEL_ROUTING : null }))}
                />
              </div>
              {formData.channel_routing && (
                <div className="border rounded p-3 mb-3">
                  {(['red', 'yellow', 'grey'] as const).map(level => (
                    <div className="mb-3" key={level}>
                      <Form.Label>{t(`form.${level}`)}</Form.Label>
                      <MultiSelectDropdown
                        options={channelOptions}
                        selectedValues={formData.channel_routing?.[level] || []}
                        onChange={(values) => setFormData(prev => ({
                          ...prev,
                          channel_routing: {
                            ...(prev.channel_routing || {}),
                            [level]: values as string[],
                          },
                        }))}
                      />
                    </div>
                  ))}
                </div>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.deliveryTiming')}</Form.Label>
                    <Form.Select value={formData.digest_mode || ''} onChange={(e) => setFormData(prev => ({ ...prev, digest_mode: e.target.value ? e.target.value as any : null }))}>
                      <option value="">{t('form.realtime')}</option>
                      <option value="daily">{t('form.dailyDigest')}</option>
                      <option value="weekly">{t('form.weeklyDigest')}</option>
                    </Form.Select>
                    <Form.Text className="text-muted">{t('form.digestHint')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.escalationMinutes')}</Form.Label>
                    <Form.Control type="number" min="1" value={formData.escalation_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, escalation_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                  </Form.Group>
                </Col>
              </Row>

              {formData.escalation_minutes && (
                <div className="mb-3">
                  <Form.Label>{t('form.escalationRecipients')}</Form.Label>
                  <MultiSelectDropdown
                    options={userOptions}
                    selectedValues={formData.escalation_user_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, escalation_user_ids: values as number[] }))}
                  />
                </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.employeeRecipients')}</Form.Label>
                <MultiSelectDropdown
                  searchable options={activeEmployeeOptions}
                  selectedValues={formData.policy_employee_recipients || []}
                  onChange={(values) => setFormData(prev => ({ ...prev, policy_employee_recipients: values as number[] }))}
                  placeholder={t('form.selectEmployees')}
                />
                <Form.Text className="text-muted">{t('form.employeeRecipientsHint')}</Form.Text>
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationCooldown')}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.repeat_interval_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, repeat_interval_minutes: Number(e.target.value) }))}
                  placeholder="180"
                />
                <Form.Text className="text-muted">
                  {t('form.cooldownHelp')}
                </Form.Text>
              </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={loading}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('status.creating')}
                </>
              ) : (
                <>
                  <i className="bi bi-plus-lg"></i>
                  {t('createPolicy')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

        {/* Edit Policy Modal */}
        <Modal show={showEditModal} onHide={() => {
          setShowEditModal(false);
          setEditingPolicy(null);
          resetForm();
        }} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('editPolicy')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
            <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.companyId')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.company_id}
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.policyName')} *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('form.enterPolicyName')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.alertLevel')} *</Form.Label>
                    <Form.Select
                      value={formData.alert_level}
                      onChange={(e) => setFormData(prev => ({ ...prev, alert_level: e.target.value as any }))}
                      required
                    >
                      <option value="grey">{t('form.grey')}</option>
                      <option value="yellow">{t('form.yellow')}</option>
                      <option value="red">{t('form.red')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('columns.status')}</Form.Label>
                    <Form.Check
                      type="switch"
                      label={t('enabled')}
                      checked={formData.is_enabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_enabled: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.actionType')} *</Form.Label>
                    <Form.Select
                      value={formData.action_type}
                      onChange={(e) => handleActionTypeChange(e.target.value as CreatePolicyRequest['action_type'])}
                    >
                      {ACTION_TYPE_GROUPS.map(group => (
                        <optgroup key={group.labelKey} label={t(group.labelKey)}>
                          {group.values.map(actionType => (
                            <option key={actionType} value={actionType}>
                              {actionTypeLabels[actionType]}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Form.Select>
                    {formData.action_type === 'former_employee_detected' && (
                      <Alert variant="info" className="mb-3">
                        {t('form.formerEmployeeDetectedHint')}
                      </Alert>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.targetedLocations')} *</Form.Label>
                    <MultiSelectDropdown
                      options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                      selectedValues={formData.locations}
                      onChange={(values) => setFormData(prev => ({ ...prev, locations: values as number[] }))}
                      placeholder={t('form.selectLocations')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.detectionMethod')} *</Form.Label>
                    <Form.Select
                      value={formData.detection_method}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        detection_method: e.target.value as any,
                        departments: [],
                        employee_ids: []
                      }))}
                    >
                      <option value="department">{t('byDepartment')}</option>
                      <option value="employee">{t('byEmployee')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {/* Time Mode for Detected */}
                {isTimeWindowAction(formData.action_type) && (
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeMode')} *</Form.Label>
                      <Form.Select
                        value={formData.time_mode}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          time_mode: e.target.value as any
                        }))}
                      >
                        <option value="anytime">{t('form.anytime')}</option>
                        <option value="within_window">{t('form.withinWindow')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {/* Timeslap for Not Detected */}
              {(formData.action_type === 'not_detected') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>{t('form.timeInterval')} *</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={formData.timeslap}
                        onChange={(e) => setFormData(prev => ({ ...prev, timeslap: Number(e.target.value) }))}
                        placeholder={t('form.timeIntervalPlaceholder')}
                      />
                      <Form.Text className="text-muted">
                        {t('form.timeIntervalHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* PPE Confidence Threshold */}
              {formData.action_type.startsWith('ppe_') && (
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>
                        {t('form.confidenceThreshold')}: {Math.round((formData.ppe_confidence_threshold ?? 0.8) * 100)}%
                      </Form.Label>
                      <Form.Range
                        min="0"
                        max="1"
                        step="0.05"
                        value={formData.ppe_confidence_threshold ?? 0.8}
                        onChange={(e) => setFormData(prev => ({ ...prev, ppe_confidence_threshold: parseFloat(e.target.value) }))}
                      />
                      <Form.Text className="text-muted">
                        {t('form.confidenceThresholdHelp')}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {(formData.action_type === 'late_arrival' || formData.action_type === 'absent_working_day' || formData.action_type === 'camera_offline' || formData.action_type === 'consecutive_absence' || formData.action_type === 'break_overrun') && (
                <Row>
                  {formData.action_type === 'late_arrival' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.graceExtraMinutes')}</Form.Label>
                        <Form.Control type="number" min="0" value={formData.grace_extra_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, grace_extra_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'absent_working_day' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.checkAfterMinutes')}</Form.Label>
                        <Form.Control type="number" min="1" value={formData.check_after_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, check_after_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'camera_offline' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.offlineThresholdMinutes')} *</Form.Label>
                        <Form.Control type="number" min="5" value={formData.offline_threshold_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, offline_threshold_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'consecutive_absence' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.consecutiveDaysThreshold')} *</Form.Label>
                        <Form.Control type="number" min="2" value={formData.consecutive_days_threshold ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, consecutive_days_threshold: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                  {formData.action_type === 'break_overrun' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.maxBreakMinutes')} *</Form.Label>
                        <Form.Control type="number" min="1" value={formData.max_break_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, max_break_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              )}

              {(formData.action_type === 'repeated_ppe_violation' || formData.action_type === 'occupancy_threshold') && (
                <Row>
                  {formData.action_type === 'repeated_ppe_violation' && (
                    <>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.violationCountThreshold')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.violation_count_threshold ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, violation_count_threshold: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.violationWindowHours')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.violation_window_hours ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, violation_window_hours: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                  {formData.action_type === 'occupancy_threshold' && (
                    <>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.maxOccupancy')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.max_occupancy ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, max_occupancy: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                      <Col md={6} className="mb-3">
                        <Form.Group>
                          <Form.Label>{t('form.occupancyWindowMinutes')} *</Form.Label>
                          <Form.Control type="number" min="1" value={formData.occupancy_window_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, occupancy_window_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                        </Form.Group>
                      </Col>
                    </>
                  )}
                </Row>
              )}

              {/* Time Window Fields */}
              {isTimeWindowAction(formData.action_type) && formData.time_mode === 'within_window' && (
                <>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.startTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.start_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.endTime')} *</Form.Label>
                        <Form.Control
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateStart')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_start}
                          min={getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_start: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>{t('form.dateEnd')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.date_end}
                          min={formData.date_start || getTodayDateInCompanyTimezone(currentCompany?.timezone || 'UTC')}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_end: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Form.Label>{t('form.daysOfWeek')}</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <Form.Check
                          key={day}
                          type="checkbox"
                          label={day}
                          checked={formData.days_of_week?.includes(day)}
                          onChange={(e) => {
                            const currentDays = formData.days_of_week || [];
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: [...currentDays, day]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                days_of_week: currentDays.filter(d => d !== day)
                              }));
                            }
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Target Selection */}
              {formData.detection_method === 'department' ? (
              <div className="mb-3">
                <Form.Label>{t('form.targetedByDepartments')} *</Form.Label>
                  <MultiSelectDropdown
                    options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                    selectedValues={formData.departments || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, departments: values as number[] }))}
                  placeholder={t('form.selectDepartments')}
                />
              </div>
              ) : (
              <div className="mb-3">
                <Form.Label>{t('form.targetEmployees')} *</Form.Label>
                  <MultiSelectDropdown
                    searchable options={employeeOptions}
                    selectedValues={formData.employee_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, employee_ids: values as number[] }))}
                  placeholder={t('form.selectEmployees')}
                />
                {formData.action_type === 'former_employee_detected' && (
                  <Form.Text className="text-muted">{t('form.formerEmployeeTargetHint')}</Form.Text>
                )}
              </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.notificationRecipients')} *</Form.Label>
                <MultiSelectDropdown
                  options={users.map(user => ({ value: user.id, label: `${user.name} (${user.email})` }))}
                  selectedValues={formData.user_ids}
                  onChange={(values) => setFormData(prev => ({ ...prev, user_ids: values as number[] }))}
                  placeholder={t('form.selectUsers')}
                />
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationChannels')} *</Form.Label>
                <MultiSelectDropdown
                  options={channelOptions}
                  selectedValues={formData.notify_channels}
                  onChange={(values) => setFormData(prev => ({ ...prev, notify_channels: values as string[] }))}
                  placeholder={t('form.selectChannels')}
                />
              </div>

              <div className="mb-3">
                <Form.Check
                  type="switch"
                  label={t('form.perLevelRouting')}
                  checked={!!formData.channel_routing}
                  onChange={(e) => setFormData(prev => ({ ...prev, channel_routing: e.target.checked ? DEFAULT_CHANNEL_ROUTING : null }))}
                />
              </div>
              {formData.channel_routing && (
                <div className="border rounded p-3 mb-3">
                  {(['red', 'yellow', 'grey'] as const).map(level => (
                    <div className="mb-3" key={level}>
                      <Form.Label>{t(`form.${level}`)}</Form.Label>
                      <MultiSelectDropdown
                        options={channelOptions}
                        selectedValues={formData.channel_routing?.[level] || []}
                        onChange={(values) => setFormData(prev => ({
                          ...prev,
                          channel_routing: {
                            ...(prev.channel_routing || {}),
                            [level]: values as string[],
                          },
                        }))}
                      />
                    </div>
                  ))}
                </div>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.deliveryTiming')}</Form.Label>
                    <Form.Select value={formData.digest_mode || ''} onChange={(e) => setFormData(prev => ({ ...prev, digest_mode: e.target.value ? e.target.value as any : null }))}>
                      <option value="">{t('form.realtime')}</option>
                      <option value="daily">{t('form.dailyDigest')}</option>
                      <option value="weekly">{t('form.weeklyDigest')}</option>
                    </Form.Select>
                    <Form.Text className="text-muted">{t('form.digestHint')}</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('form.escalationMinutes')}</Form.Label>
                    <Form.Control type="number" min="1" value={formData.escalation_minutes ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, escalation_minutes: e.target.value === '' ? null : Number(e.target.value) }))} />
                  </Form.Group>
                </Col>
              </Row>

              {formData.escalation_minutes && (
                <div className="mb-3">
                  <Form.Label>{t('form.escalationRecipients')}</Form.Label>
                  <MultiSelectDropdown
                    options={userOptions}
                    selectedValues={formData.escalation_user_ids || []}
                    onChange={(values) => setFormData(prev => ({ ...prev, escalation_user_ids: values as number[] }))}
                  />
                </div>
              )}

              <div className="mb-3">
                <Form.Label>{t('form.employeeRecipients')}</Form.Label>
                <MultiSelectDropdown
                  searchable options={activeEmployeeOptions}
                  selectedValues={formData.policy_employee_recipients || []}
                  onChange={(values) => setFormData(prev => ({ ...prev, policy_employee_recipients: values as number[] }))}
                  placeholder={t('form.selectEmployees')}
                />
                <Form.Text className="text-muted">{t('form.employeeRecipientsHint')}</Form.Text>
              </div>

              <div className="mb-3">
                <Form.Label>{t('form.notificationCooldown')}</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={formData.repeat_interval_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, repeat_interval_minutes: Number(e.target.value) }))}
                  placeholder="180"
                />
                <Form.Text className="text-muted">
                  {t('form.cooldownHelp')}
                </Form.Text>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPolicy(null);
                  resetForm();
                }}
                disabled={loading}
              >
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {tc('status.updating')}
                  </>
                ) : (
                  <>
                    <i className="bi bi-pencil"></i>
                    {t('updatePolicy')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* View Policy Modal */}
        <Modal show={showViewModal} onHide={closeViewModal} size="lg" centered scrollable>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('policyDetails')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {viewModalLoading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="me-3" />
                <span>{t('loadingDetails')}</span>
              </div>
            ) : viewingPolicy ? (
              <div>
                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.policyName')}</label>
                      <p className="mb-0">{viewingPolicy.name}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('columns.status')}</label>
                      <div>
                        <Badge bg={viewingPolicy.is_enabled ? 'success' : 'secondary'}>
                          {viewingPolicy.is_enabled ? t('enabled') : t('disabled')}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.alertLevel')}</label>
                      <div>
                        <Badge bg={viewingPolicy.alert_level === 'red' ? 'danger' : 
                                 viewingPolicy.alert_level === 'yellow' ? 'warning' : 'secondary'}>
                          {viewingPolicy.alert_level.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.actionType')}</label>
                      <p className="mb-0">{actionTypeLabels[viewingPolicy.action_type] || viewingPolicy.action_type}</p>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.detectionMethod')}</label>
                      <p className="mb-0">{viewingPolicy.detection_method === 'department' ? t('byDepartment') : t('byEmployee')}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.timeMode')}</label>
                      <p className="mb-0">{viewingPolicy.time_mode === 'anytime' ? t('form.anytime') : t('form.withinWindow')}</p>
                    </div>
                  </Col>
                </Row>

                {isTimeWindowAction(viewingPolicy.action_type) && viewingPolicy.time_mode === 'within_window' && (
                  <>
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.startTime')}</label>
                          <p className="mb-0">{viewingPolicy.start_time}</p>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.endTime')}</label>
                          <p className="mb-0">{viewingPolicy.end_time}</p>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.dateStart')}</label>
                          <p className="mb-0">{viewingPolicy.date_start || t('notSet')}</p>
                        </div>
                      </Col>
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.dateEnd')}</label>
                          <p className="mb-0">{viewingPolicy.date_end || t('notSet')}</p>
                        </div>
                      </Col>
                    </Row>

                    <div className="mb-3">
                      <label className="fw-semibold text-muted small">{t('form.daysOfWeek')}</label>
                      <p className="mb-0">
                        {Array.isArray(viewingPolicy.days_of_week) 
                          ? viewingPolicy.days_of_week.join(', ') 
                          : viewingPolicy.days_of_week || t('none')}
                      </p>
                    </div>
                  </>
                )}

                {(viewingPolicy.action_type === 'not_detected' || viewingPolicy.action_type.startsWith('ppe_')) && (
                  <Row className="mb-3">
                    <Col xs={12} md={6}>
                      <div className="mb-3">
                        <label className="fw-semibold text-muted small">{t('form.timeInterval')}</label>
                        <p className="mb-0">{viewingPolicy.timeslap || 15} {t('minutes')}</p>
                      </div>
                    </Col>
                    {viewingPolicy.action_type.startsWith('ppe_') && (
                      <Col xs={12} md={6}>
                        <div className="mb-3">
                          <label className="fw-semibold text-muted small">{t('form.confidenceThreshold')}</label>
                          <p className="mb-0">
                            {viewingPolicy.ppe_confidence_threshold != null 
                              ? `${Math.round(viewingPolicy.ppe_confidence_threshold * 100)}%` 
                              : t('form.confidenceThresholdAny')}
                          </p>
                        </div>
                      </Col>
                    )}
                  </Row>
                )}

                <Row className="mb-3">
                  {TYPE_SPECIFIC_FIELDS.filter(field => (viewingPolicy as any)[field] !== null && (viewingPolicy as any)[field] !== undefined).map(field => (
                    <Col xs={12} md={6} key={field}>
                      <div className="mb-3">
                        <label className="fw-semibold text-muted small">{t(`form.${String(field).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}</label>
                        <p className="mb-0">{String((viewingPolicy as any)[field])}</p>
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('columns.locations')}</label>
                  <div>
                    {viewingPolicy.locations && viewingPolicy.locations.length > 0 ? (
                      viewingPolicy.locations.map((location, index) => (
                        <Badge key={index} bg="info" className="me-2 mb-2">
                          {location.department_name}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-muted mb-0">{t('noLocationsSelected')}</p>
                    )}
                  </div>
                </div>

                {viewingPolicy.detection_method === 'department' && (
                  <div className="mb-3">
                    <label className="fw-semibold text-muted small">{t('targetDepartments')}</label>
                    <div>
                      {viewingPolicy.departments && viewingPolicy.departments.length > 0 ? (
                        viewingPolicy.departments.map((dept, index) => (
                          <Badge key={index} bg="primary" className="me-2 mb-2">
                            {dept.department_name}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-muted mb-0">{t('noDepartmentsSelected')}</p>
                      )}
                    </div>
                  </div>
                )}

                {viewingPolicy.detection_method === 'employee' && (
                  <div className="mb-3">
                    <label className="fw-semibold text-muted small">{t('form.targetEmployees')}</label>
                    <div>
                      {viewingPolicy.targets && viewingPolicy.targets.length > 0 ? (
                        viewingPolicy.targets.map((target, index) => (
                          <Badge key={index} bg="success" className="me-2 mb-2">
                            {target.name} ({target.emp_id})
                          </Badge>
                        ))
                      ) : (
                        <p className="text-muted mb-0">{t('noEmployeesSelected')}</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationRecipients')}</label>
                  <p className="mb-0">
                    {viewingPolicy.recipients && viewingPolicy.recipients.length > 0 ? (
                      <span>{t('userCount', { count: viewingPolicy.recipients.length })}</span>
                    ) : (
                      <span className="text-muted">{t('noRecipientsSelected')}</span>
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationChannels')}</label>
                  <div>
                    {viewingPolicy.notify_channels && viewingPolicy.notify_channels.length > 0 ? (
                      viewingPolicy.notify_channels.map((channel, index) => (
                        <Badge key={index} bg="secondary" className="me-2 mb-2">
                          {t(CHANNEL_LABEL_KEYS[channel] || channel)}
                        </Badge>
                      ))
                    ) : (
                      <p className="text-muted mb-0">{t('noChannelsSelected')}</p>
                    )}
                  </div>
                </div>

                {viewingPolicy.channel_routing && (
                  <div className="mb-3">
                    <label className="fw-semibold text-muted small">{t('form.perLevelRouting')}</label>
                    {(['red', 'yellow', 'grey'] as const).map(level => (
                      <p className="mb-1" key={level}>
                        <strong>{t(`form.${level}`)}:</strong> {(viewingPolicy.channel_routing?.[level] || []).map(channel => t(CHANNEL_LABEL_KEYS[channel] || channel)).join(', ') || t('none')}
                      </p>
                    ))}
                  </div>
                )}

                {(viewingPolicy.digest_mode || viewingPolicy.escalation_minutes || viewingPolicy.escalation_user_ids?.length || viewingPolicy.policy_employee_recipients?.length) && (
                  <Row className="mb-3">
                    {viewingPolicy.digest_mode && (
                      <Col xs={12} md={4}>
                        <label className="fw-semibold text-muted small">{t('form.deliveryTiming')}</label>
                        <p>{viewingPolicy.digest_mode === 'daily' ? t('form.dailyDigest') : t('form.weeklyDigest')}</p>
                      </Col>
                    )}
                    {viewingPolicy.escalation_minutes && (
                      <Col xs={12} md={4}>
                        <label className="fw-semibold text-muted small">{t('form.escalationMinutes')}</label>
                        <p>{viewingPolicy.escalation_minutes} {t('minutes')}</p>
                      </Col>
                    )}
                    {viewingPolicy.escalation_user_ids?.length ? (
                      <Col xs={12} md={4}>
                        <label className="fw-semibold text-muted small">{t('form.escalationRecipients')}</label>
                        <div>
                          {viewingPolicy.escalation_user_ids.map((userId) => {
                            const escalationUser = users.find(user => user.id === userId);
                            return (
                              <Badge key={userId} bg="warning" text="dark" className="me-2 mb-2">
                                {escalationUser ? `${escalationUser.name} (${escalationUser.email})` : `#${userId}`}
                              </Badge>
                            );
                          })}
                        </div>
                      </Col>
                    ) : null}
                    {viewingPolicy.policy_employee_recipients?.length ? (
                      <Col xs={12} md={4}>
                        <label className="fw-semibold text-muted small">{t('form.employeeRecipients')}</label>
                        <p>{t('empCount', { count: viewingPolicy.policy_employee_recipients.length })}</p>
                      </Col>
                    ) : null}
                  </Row>
                )}

                <div className="mb-3">
                  <label className="fw-semibold text-muted small">{t('form.notificationCooldown')}</label>
                  <p className="mb-0">
                    {viewingPolicy.repeat_interval_minutes || 180} {t('minutes')} 
                    {viewingPolicy.repeat_interval_minutes === 180 && <span className="text-muted"> ({t('default')})</span>}
                  </p>
                  <Form.Text className="text-muted small">
                    {t('form.cooldownHelp')}
                  </Form.Text>
                </div>

                <Row className="mb-0">
                  <Col xs={12} md={6}>
                    <div className="mb-3 mb-md-0">
                      <label className="fw-semibold text-muted small">{tc('labels.createdAt')}</label>
                      <p className="mb-0">{formatDateTimeInCompanyTimezone(viewingPolicy.created_at, currentCompany?.timezone || 'UTC')}</p>
                    </div>
                  </Col>
                  <Col xs={12} md={6}>
                    <div>
                      <label className="fw-semibold text-muted small">{tc('labels.updatedAt')}</label>
                      <p className="mb-0">{formatDateTimeInCompanyTimezone(viewingPolicy.updated_at, currentCompany?.timezone || 'UTC')}</p>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">{t('failedToLoad')}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeViewModal}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Policy Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => {
          setShowDeleteModal(false);
          setDeletingPolicy(null);
        }} centered>
          <Modal.Header closeButton className="py-2 py-md-3">
            <Modal.Title className="h5 mb-0">{t('deletePolicy')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-2 py-md-3">
            <p>{t('deleteConfirm', { name: deletingPolicy?.name })}</p>
            <p className="text-muted mb-0">{tc('messages.cannotUndo')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingPolicy(null);
              }}
              disabled={loading}
            >
              {tc('buttons.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeletePolicy} 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {tc('status.deleting')}
                </>
              ) : (
                <>
                  <i className="bi bi-trash"></i>
                  {t('deletePolicy')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Policies;

